// SCR-STAFF-616: فیلدهای مشترک (US-DOC-152).
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import { unwrap } from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, FormDialog, ListPage, type DataTableColumn } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { docKeys, SCR_STAFF_616 } from './common';

interface SharedFieldView {
  id: string;
  shareKey: string;
  questionType: string;
  sensitivity: string;
  label: string;
  isActive: boolean;
}

const QUESTION_TYPES = ['text', 'number', 'boolean', 'date', 'select', 'multiselect'] as const;

export function SharedFieldsPage() {
  const { t } = useTranslation('doc');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const key = useIdempotencyKey();
  const canManage = access.accessMode === 'Full' && access.can('doc.shared_field.manage');
  const [editing, setEditing] = useState<SharedFieldView | 'new' | null>(null);
  const [form, setForm] = useState({ shareKey: '', questionType: 'text', sensitivity: 'Clinical', label: '', isActive: true });

  const list = useQuery({
    queryKey: docKeys.sharedFields(session?.tenantId),
    enabled: canManage,
    queryFn: async () => (await unwrap(tenantApi.GET('/api/v1/documents/shared-fields', {}))) as SharedFieldView[],
  });

  const open = (row: SharedFieldView | 'new') => {
    if (row === 'new') {
      setForm({ shareKey: '', questionType: 'text', sensitivity: 'Clinical', label: '', isActive: true });
    } else {
      setForm({
        shareKey: row.shareKey,
        questionType: row.questionType,
        sensitivity: row.sensitivity,
        label: row.label,
        isActive: row.isActive,
      });
    }
    setEditing(row);
  };

  const save = useMutation({
    mutationFn: () => {
      const body = {
        shareKey: form.shareKey.trim(),
        questionType: form.questionType,
        sensitivity: form.sensitivity,
        label: form.label.trim(),
        isActive: form.isActive,
      };
      return editing && editing !== 'new'
        ? unwrap(
            tenantApi.PUT('/api/v1/documents/shared-fields/{id}', {
              params: { path: { id: editing.id } },
              body,
            }),
          )
        : unwrap(
            tenantApi.POST('/api/v1/documents/shared-fields', {
              headers: { 'Idempotency-Key': key.current() },
              body,
            }),
          );
    },
    onSettled: (_data, error) => key.settle(error ?? undefined),
    onSuccess: () => {
      setEditing(null);
      void queryClient.invalidateQueries({ queryKey: docKeys.sharedFields(session?.tenantId) });
    },
  });

  const columns: DataTableColumn<SharedFieldView>[] = [
    { id: 'key', headerKey: 'doc:sharedFields.shareKey', cell: (row) => <bdi dir="ltr">{row.shareKey}</bdi> },
    { id: 'type', headerKey: 'doc:sharedFields.questionType', cell: (row) => row.questionType },
    { id: 'label', headerKey: 'doc:sharedFields.label', cell: (row) => row.label },
    { id: 'sensitivity', headerKey: 'doc:sharedFields.sensitivity', cell: (row) => t(`doc:sensitivity.${row.sensitivity}`, { defaultValue: row.sensitivity }) },
    { id: 'active', headerKey: 'doc:sharedFields.active', cell: (row) => (row.isActive ? t('doc:settings.active') : '—') },
    {
      id: 'actions',
      headerKey: 'doc:sharedFields.actions',
      cell: (row) =>
        canManage ? (
          <Button size="small" onClick={() => open(row)} data-testid="doc-shared-field-edit">
            {t('sharedFields.edit')}
          </Button>
        ) : null,
    },
  ];

  return (
    <ListPage
      screenId={SCR_STAFF_616}
      titleKey="doc:sharedFields.title"
      actions={
        canManage ? (
          <Button variant="contained" onClick={() => open('new')} data-testid="doc-shared-field-create">
            {t('sharedFields.create')}
          </Button>
        ) : undefined
      }
    >
      <DataTable
        captionKey="doc:sharedFields.title"
        columns={columns}
        rows={list.data}
        getRowId={(row) => row.id}
        isLoading={list.isPending}
        error={list.error}
        onRetry={() => void list.refetch()}
        emptyTitleKey="doc:sharedFields.empty"
      />
      <FormDialog
        open={editing !== null}
        titleKey={editing === 'new' ? 'doc:sharedFields.create' : 'doc:sharedFields.edit'}
        onSubmit={() => save.mutate()}
        onClose={() => setEditing(null)}
        isDirty
        isSubmitting={save.isPending}
        submitError={save.error}
      >
        <TextField
          label={t('sharedFields.shareKey')}
          required
          value={form.shareKey}
          onChange={(event) => setForm((current) => ({ ...current, shareKey: event.target.value }))}
          disabled={editing !== 'new'}
          slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'doc-shared-field-key' } }}
        />
        <TextField
          select
          label={t('sharedFields.questionType')}
          value={form.questionType}
          onChange={(event) => setForm((current) => ({ ...current, questionType: event.target.value }))}
        >
          {QUESTION_TYPES.map((value) => (
            <MenuItem key={value} value={value}>
              {value}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label={t('sharedFields.label')}
          required
          value={form.label}
          onChange={(event) => setForm((current) => ({ ...current, label: event.target.value }))}
        />
        <TextField
          select
          label={t('sharedFields.sensitivity')}
          value={form.sensitivity}
          onChange={(event) => setForm((current) => ({ ...current, sensitivity: event.target.value }))}
        >
          {['General', 'Clinical', 'HighlySensitive'].map((value) => (
            <MenuItem key={value} value={value}>
              {t(`doc:sensitivity.${value}`)}
            </MenuItem>
          ))}
        </TextField>
        <FormControlLabel
          control={<Switch checked={form.isActive} onChange={(event) => setForm((current) => ({ ...current, isActive: event.target.checked }))} />}
          label={t('sharedFields.active')}
        />
      </FormDialog>
    </ListPage>
  );
}
